import {
  getAdminV1OrgProducts,
  getAdminV1OrgProductsTags,
  getAdminV1OrgProductsVendors,
  getAdminV1OrgSchedules,
  getAdminV1StoresStoreIdCollections,
  getAdminV1StoresStoreIdCollectionsId,
  getAdminV1StoresStoreIdProducts,
  getAdminV1StoresStoreIdProductsIdPricing,
  postAdminV1OrgProductsLookup,
  postAdminV1StoresStoreIdProductsLookup,
  type Collection,
  type GetAdminV1OrgProductsParams,
  type GetAdminV1OrgSchedules200,
  type GetAdminV1StoresStoreIdCollections200,
  type GetAdminV1StoresStoreIdCollectionsParams,
  type GetAdminV1StoresStoreIdProductsParams,
  type ProductFacetList,
  type ProductListPage,
  type ProductLookupRequest,
  type ProductLookupResponse,
  type StoreProductListPage,
  type StoreVariantPricing,
} from '@nucleus-commerce/api-client/admin';

/**
 * `attr.<key>=a,b` params: the backend reads them from the query string, the OpenAPI spec cannot
 * list them (they are per organization), so the generated params type lacks them.
 */
type AttributeParams = Partial<Record<`attr.${string}`, string>>;

export type OrgProductListParams = GetAdminV1OrgProductsParams & AttributeParams;
export type StoreProductListParams = GetAdminV1StoresStoreIdProductsParams & AttributeParams;

/**
 * Reads behind the resource pickers. The pickers live in their own feature so that the features
 * using them (store catalog, collections, products) never import each other through it; the
 * calls are therefore made here rather than through those features' doors.
 */
export const resourcePickersApi = {
  products: (params: OrgProductListParams, signal: AbortSignal): Promise<ProductListPage> =>
    getAdminV1OrgProducts(params, { signal }),
  storeProducts: (
    storeId: string,
    params: StoreProductListParams,
    signal: AbortSignal,
  ): Promise<StoreProductListPage> =>
    getAdminV1StoresStoreIdProducts({ storeId }, params, { signal }),
  /** Pasted SKUs, handles, barcodes or ids, in the master catalog or one store's. */
  lookup: (
    storeId: string | null,
    body: ProductLookupRequest,
    signal?: AbortSignal,
  ): Promise<ProductLookupResponse> =>
    storeId
      ? postAdminV1StoresStoreIdProductsLookup({ storeId }, body, { signal })
      : postAdminV1OrgProductsLookup(body, { signal }),
  tags: (q: string, signal: AbortSignal): Promise<ProductFacetList> =>
    getAdminV1OrgProductsTags({ limit: 50, ...(q ? { q } : {}) }, { signal }),
  vendors: (q: string, signal: AbortSignal): Promise<ProductFacetList> =>
    getAdminV1OrgProductsVendors({ limit: 50, ...(q ? { q } : {}) }, { signal }),
  /**
   * Rotations for the store scope's schedule filter: the ones targeting this store that have not
   * ended (an ended rotation owns nothing), matching the picker's text.
   */
  schedules: (
    storeId: string,
    q: string,
    signal: AbortSignal,
  ): Promise<GetAdminV1OrgSchedules200> =>
    getAdminV1OrgSchedules(
      { storeId, status: 'draft,active,paused', limit: 50, ...(q ? { q } : {}) },
      { signal },
    ),
  storeVariants: (
    storeId: string,
    storeProductId: string,
    signal: AbortSignal,
  ): Promise<StoreVariantPricing[]> =>
    getAdminV1StoresStoreIdProductsIdPricing({ storeId, id: storeProductId }, { signal }).then(
      (r) => r.items,
    ),
  collections: (
    storeId: string,
    params: GetAdminV1StoresStoreIdCollectionsParams,
    signal: AbortSignal,
  ): Promise<GetAdminV1StoresStoreIdCollections200> =>
    getAdminV1StoresStoreIdCollections({ storeId }, params, { signal }),
  collection: (storeId: string, id: string, signal: AbortSignal): Promise<Collection> =>
    getAdminV1StoresStoreIdCollectionsId({ storeId, id }, { signal }),
};
